import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import type { Variant } from "@/components/ui/VariantsManager";
import { ProductImage } from "@/components/ui/ProductImage";
import { AnnouncementRibbon, StatusBadge } from "@/components/ui/ProductCard";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useVariantImagesMap } from "@/lib/variantImages";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLanguage } from "@/hooks/useLanguage";
import { pickLocalized } from "@/lib/localized";
import { hasProductOrigin, requestProductReturn } from "@/lib/productNavigation";
import NotFound from "./NotFound";

type Product = {
  id: string;
  name: string;
  name_en: string | null;
  description: string;
  description_en: string | null;
  slug: string;
  brand: { name: string };
  status: { name: string };
  announcement: {
    id: string;
    message: string;
    message_en: string | null;
    text_color: string | null;
    background_color: string | null;
  } | null;
  variants: Variant[];
};

export default function ProductDetail() {
  const { t } = useTranslation();
  const { public_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isSuperuser = !!user?.is_superuser;
  const { currentLanguage } = useLanguage();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const requestedVariantId = Number(searchParams.get("variant"));

  const displayName = product ? pickLocalized(product.name, product.name_en, currentLanguage) : null;
  const displayDescription = product ? pickLocalized(product.description, product.description_en, currentLanguage) : null;

  usePageTitle(displayName);

  useEffect(() => {
    // React Router preserves the current document scroll position between
    // routes. Product details should always open at their own top; the
    // product-list position is saved separately and restored by Back.
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [public_id]);

  useEffect(() => {
    if (authLoading) return;
    const endpoint = isSuperuser ? `/admin/product/${public_id}` : `/product/${public_id}`;
    apiFetch(endpoint)
      .then((res) => {
        if (res.status === 404 || res.status === 422) {
          setNotFound(true);
          return null;
        }
        if (!res.ok) throw new Error(t("productDetail.couldNotLoadProduct"));
        return res.json();
      })
      .then((data) => {
        if (data) setProduct(data);
      })
      .catch(() => setError(t("productDetail.couldNotLoadProduct")));
  }, [public_id, authLoading, isSuperuser, t]);

  useEffect(() => {
    const requestedVariant = Number.isFinite(requestedVariantId)
      ? product?.variants.find(variant => variant.id === requestedVariantId)
      : undefined;
    setSelectedVariantId(requestedVariant?.id ?? product?.variants[0]?.id ?? null);
  }, [product, requestedVariantId]);

  const imageUrls = useVariantImagesMap(product?.variants.map(v => v.id) ?? []);

  useEffect(() => {
    Object.values(imageUrls).forEach(url => {
      if (url) new Image().src = url;
    });
  }, [imageUrls]);

  const onDelete = async () => {
    if (!product || !confirm(t("productDetail.confirmDelete", { name: product.name }))) return;

    setDeleting(true);
    try {
      const res = await apiFetch(`/product/${product.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(t("productDetail.couldNotDeleteProduct"));
      navigate("/products");
    } catch {
      setError(t("productDetail.couldNotDeleteProduct"));
      setDeleting(false);
    }
  };

  const onBack = () => {
    if (hasProductOrigin()) {
      requestProductReturn();
      navigate(-1);
      return;
    }

    navigate("/products");
  };

  if (notFound) return <NotFound />;
  if (error) return <div>{error}</div>;
  if (!product) return <ProductDetailSkeleton />;

  const selectedVariant = product.variants.find(v => v.id === selectedVariantId) ?? product.variants[0];
  const selectedAnnouncement = selectedVariant?.announcement === undefined
    ? product.announcement
    : selectedVariant.announcement;

  return (
    <div className="max-w-[1280px] mx-auto w-full pb-10 relative">
      <div className="flex flex-wrap items-center justify-between gap-3 px-2 sm:px-0">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-app-border bg-app-control px-4 py-2 text-sm font-semibold text-brand shadow-sm transition-colors hover:bg-app-control-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand cursor-pointer"
          aria-label={t("productDetail.back")}
        >
          <ArrowLeft className="size-5" aria-hidden="true" />
          <span>{t("productDetail.back")}</span>
        </button>
        {isSuperuser && (
          <div className="flex flex-wrap justify-end gap-2">
          <Link to={`/product/${product.slug}/p/${product.id}/edit`} className="rounded-full px-4 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand/90 transition-colors duration-200">
            {t("common.edit")}
          </Link>
          <button
            type="button"
            className="rounded-full px-4 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors duration-200 cursor-pointer disabled:opacity-50"
            onClick={onDelete}
            disabled={deleting}
          >
            {deleting ? t("common.deleting") : t("common.delete")}
          </button>
          </div>
        )}
      </div>
      <div className="flex lg:flex-row flex-col gap-8 lg:gap-20 w-full justify-center items-center lg:items-start pt-8">
        <div className="relative flex items-center justify-center shadow-lg rounded-2xl p-6 w-full max-w-[350px] lg:max-w-none lg:w-[450px] aspect-square bg-white">
          <AnnouncementRibbon announcement={selectedAnnouncement} size="lg" />
          <StatusBadge status={selectedVariant?.is_unavailable ? "unavailable" : product.status.name} />
          <ProductImage
            src={selectedVariant ? imageUrls[selectedVariant.id] : null}
            alt={displayName ?? ""}
            className="w-full h-full rounded-2xl"
          />
        </div>
        <div className="flex flex-col gap-5 items-center lg:items-start max-w-lg">
          <div className="flex flex-col gap-0 lg:items-start max-w-lg">
          <h1 className="text-2xl lg:text-4xl font-bold text-brand text-center lg:text-left">{displayName}</h1>
          {selectedVariant && (
            <p className="text-xl text-brand/60 text-center lg:text-left">
              {t("variant.sku")}: {selectedVariant.sku}
            </p>
          )}
          </div>
        {isSuperuser && selectedVariant && (
          <p className="text-2xl font-bold text-brand/80">
            {Number(selectedVariant.price).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </p>
        )}

          {product.variants.length > 0 && (
            <div className="flex flex-wrap justify-center lg:justify-start gap-3">
              {product.variants.map(variant => {
                const label = [variant.color, variant.flavour, variant.size].filter(Boolean).join(" / ") || t("productDetail.option");
                const isSelected = selectedVariantId === variant.id;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => {
                      setSelectedVariantId(variant.id);
                      setSearchParams({ variant: String(variant.id) }, { replace: true });
                    }}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 cursor-pointer border-2 text-base ${
                      isSelected
                        ? "bg-brand text-white border-brand shadow-lg scale-105"
                        : "bg-app-control text-brand border-app-border hover:border-brand hover:bg-app-control-hover"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="border-t border-app-border w-full" />
          <div className="flex flex-col gap-1">
            <p className="text-base lg:text-lg text-brand/80 text-center lg:text-left">{displayDescription}</p>
            <p className="text-xl text-brand/80 font-bold italic text-center lg:text-left">{product.brand.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductDetailSkeleton() {
  return (
    <div className="max-w-[1280px] mx-auto w-full pb-10">
      <div className="flex lg:flex-row flex-col gap-8 lg:gap-20 w-full justify-center items-center lg:items-start pt-10">
        <div className="w-full max-w-[350px] lg:max-w-none lg:w-[450px] aspect-square rounded-2xl bg-gray-200 animate-pulse" />
        <div className="flex flex-col gap-5 items-center lg:items-start max-w-lg w-full">
          <div className="flex flex-col gap-2 items-center lg:items-start w-full">
            <div className="h-8 lg:h-10 w-3/4 rounded bg-gray-200 animate-pulse" />
            <div className="h-6 w-1/3 rounded bg-gray-200 animate-pulse" />
          </div>

          <div className="flex flex-wrap justify-center lg:justify-start gap-3">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-11 w-24 rounded-lg bg-gray-200 animate-pulse" />
            ))}
          </div>

          <div className="border-t border-gray-200 w-full" />
          <div className="flex flex-col gap-2 w-full">
            <div className="h-4 w-full rounded bg-gray-200 animate-pulse" />
            <div className="h-4 w-5/6 rounded bg-gray-200 animate-pulse" />
            <div className="h-5 w-1/4 rounded bg-gray-200 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}
